import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingDown, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const COLORS = ["#2A1810", "#C5A059", "#B91C1C", "#065F46", "#3730A3"];

export default function ShelfLifeTrend({ experimentId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["stability-trends", experimentId],
    queryFn: async () => (await api.get("/stability-tests/trends", { params: { experiment_id: experimentId } })).data,
  });

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="shelf-life-trend-card">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-[#F4EFEA]">
        <TrendingDown size={16} className="text-[#C5A059]" />
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">Shelf-Life Quality Trend</h2>
      </div>

      <div className="px-6 py-5">
        {isLoading ? (
          <p className="text-sm text-[#8C7A6B]">Loading trend…</p>
        ) : !data?.points ? (
          <p className="text-sm text-[#8C7A6B]" data-testid="trend-empty">
            No quality scores recorded yet. Add a 0–10 quality score to each stability record and the drift curve
            will appear here, one line per storage condition.
          </p>
        ) : (
          <>
            <div className="h-64" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.series} margin={{ top: 5, right: 12, left: -18, bottom: 5 }}>
                  <CartesianGrid stroke="#F4EFEA" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: "#8C7A6B", fontSize: 11 }}
                    stroke="#E8DFD1"
                    label={{ value: "Days in storage", position: "insideBottom", offset: -2, fill: "#8C7A6B", fontSize: 11 }}
                  />
                  <YAxis domain={[0, 10]} tick={{ fill: "#8C7A6B", fontSize: 11 }} stroke="#E8DFD1" />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E8DFD1", borderRadius: 8, fontSize: 12 }}
                    labelFormatter={(d) => `Day ${d}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
                  {data.conditions.map((condition, i) => (
                    <Line
                      key={condition}
                      type="monotone"
                      dataKey={condition}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 4, strokeWidth: 0, fill: COLORS[i % COLORS.length] }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-[#8C7A6B] mt-3">
              Quality score 0–10 per checkpoint, one line per storage condition. A steep drop between checkpoints is
              early evidence of bloom, texture or aroma drift.
            </p>
            {data.issues.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="trend-issues">
                {data.issues.map((issue, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2.5 text-xs text-[#B91C1C] leading-relaxed"
                    data-testid={`trend-issue-${i}`}
                  >
                    <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                    <span>
                      <span className="font-semibold">Day {issue.day} · {issue.storage_condition}:</span>{" "}
                      {issue.observations}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
